// «بیماران» — what both surfaces of the list agree about.
//
// The toolbar's vocabulary (which status chips, which sorts), the URL contract,
// and the sentence the delete confirmation has to be able to say. Pure and
// React-free: tests/screens.test.jsx reads it directly, and so does the mobile
// surface, which renders cards rather than a table but filters, sorts and
// deletes by exactly the same rules.

import { CASE_STATUSES } from '../../constants/vocabulary.js';
import { DEFAULT_STATUSES, SORTS, DEFAULT_SORT } from '../../hooks/useCases.js';

export { CASE_STATUSES, DEFAULT_STATUSES, SORTS, DEFAULT_SORT };

/**
 * The status filter row: the three real statuses plus «همه».
 *
 * «همه» is not a fourth status — it is the empty selection, which the wire reads
 * as "do not filter". Modelling it as a chip that CLEARS the set keeps one
 * concept ("which statuses am I looking at?") instead of two.
 */
export const STATUS_CHIPS = [
  { id: 'active', labelKey: 'patients.filters.active' },
  { id: 'draft', labelKey: 'patients.filters.draft' },
  { id: 'archived', labelKey: 'patients.filters.archived' },
];

export const SORT_LABEL_KEYS = {
  lastVisit: 'patients.sort.lastVisit',
  newest: 'patients.sort.newest',
  name: 'patients.sort.name',
};

/** Toggle one status chip. An empty result means «همه» — never "show nothing". */
export function toggleStatus(statuses, id) {
  const set = new Set(statuses || []);
  if (set.has(id)) set.delete(id);
  else set.add(id);
  return CASE_STATUSES.filter((s) => set.has(s));
}

/** Is the list showing everything? (The «همه» chip's pressed state.) */
export const showingAll = (statuses) => !statuses || statuses.length === 0;

/**
 * Is the emptiness the student's (no patients at all) or the filter's?
 *
 * Two different screens with two different icons and two different recoveries
 * (core.md §7.3): `UsersRound` + «بیمار جدید» for the first, `SearchX` +
 * «پاک کردن فیلتر» for the second. Telling a student who has forty patients
 * that they have none — because they typed a name with a typo — is the failure
 * this distinction exists to prevent.
 *
 * `total` is the UNFILTERED answer — "does this student have any patient in any
 * status" (`useCases().hasAny`, or a count) — and never the loaded page's
 * length: the status chips are a WIRE argument, so a page can be empty because
 * of them while the student has forty patients. A number or a boolean both
 * read as "is there anything at all".
 */
export const isFilteredEmpty = (total, visibleCount) =>
  Boolean(total) && Number(total) !== 0 && visibleCount === 0;

/** The opposite verdict: nothing in any status. Only true once `total` is KNOWN. */
export const isTrulyEmpty = (total, settled = true) =>
  settled && (total === false || Number(total) === 0);

/**
 * What `deleteCase` is about to take with it.
 *
 * The confirmation NAMES these counts (core.md §7.3). "Are you sure?" over a
 * patient carrying nine history entries, two visits and four conversations is a
 * question the student cannot answer, because the dialog has not told them what
 * they are answering about. Every count comes from the server's own count
 * fields — never `list.length`, which is empty by contract on a list row.
 */
export const deletionCounts = (row) => ({
  entries: Number(row?.entryCount || 0),
  visits: Number(row?.encounterCount || 0),
  threads: Number(row?.threadCount || 0),
});

/** Does this patient have a visit open right now? */
export const hasOpenVisit = (row) => Boolean(row?.openEncounterId);

/** An archived patient is readable, but cannot start a visit (core.md §6, rule 8). */
export const isArchived = (row) => row?.status === 'archived';
